import axios from 'axios';
import Vue from 'vue';
import { AutoModeNextItemResponse } from '@/models/AutoModeTypes';
import { getUserSetting, saveUserSetting, deleteUserSetting } from "@/services/UserSettingsService";

const AUTO_MODE_SPECIFIC_QUEUES_KEY = 'autoModeSpecificQueues';
const AUTO_MODE_STORE_FILTERS_KEY = 'autoModeStoreFilters';
const AUTO_MODE_PROGRAM_FILTERS_KEY = 'autoModeProgramFilters';
const AUTO_MODE_EXCLUDE_DISPENSE_ERRORS_KEY = 'autoModeExcludeDispenseErrors';

export interface AutoModeFilterOption {
    id: number;
    name: string;
}

export interface AutoModeFilters {
    queues?: string[];
    storeIds?: number[];
    programIds?: number[];
    excludeDispenseErrors?: boolean;
    includeOnHold?: boolean;
    onHoldSubStatusId?: number;
    showFutureDated?: boolean;
}

class AutoModeService {
    /**
     * Get the list of queues the current user has permission to access
     * @returns Promise with array of queue names
     */
    static async getAvailableQueues(): Promise<string[]> {
        try {
            const response = await axios.get<string[]>('/api/AutoMode/available-queues');
            return response.data;
        } catch (error) {
            console.error('Error getting available queues:', error);
            throw error;
        }
    }

    /**
     * Get the next available queue item for the current user
     * @param currentQueueType - Optional queue type of the current item
     * @param currentItemId - Optional ID of the current item to find the next item after
     * @param filters - Optional filters for queues, stores, and programs
     * @returns Promise with the next item response
     */
    static async getNextItem(
        currentQueueType?: string,
        currentItemId?: string | number | null,
        filters?: AutoModeFilters,
        excludeIngestionItemIds?: Array<string | number>,
        excludeOnlineOrderItemIds?: Array<string | number>
    ): Promise<AutoModeNextItemResponse> {
        try {
            const params = new URLSearchParams();
            if (currentQueueType) {
                params.append('currentQueueType', currentQueueType);
            }
            if (excludeIngestionItemIds?.length) {
                params.append('excludeIngestionIds', excludeIngestionItemIds.join(','));
            }
            if (excludeOnlineOrderItemIds?.length) {
                params.append('excludeOnlineOrderIds', excludeOnlineOrderItemIds.join(','));
            }
            if (currentItemId !== undefined && currentItemId !== null) {
                params.append('currentItemId', String(currentItemId));
            }
            if (filters?.queues?.length) {
                params.append('specificQueues', filters.queues.join(','));
            }
            if (filters?.storeIds?.length) {
                params.append('storeIds', filters.storeIds.join(','));
            }
            if (filters?.programIds?.length) {
                params.append('programIds', filters.programIds.join(','));
            }
            if (filters?.excludeDispenseErrors) {
                params.append('excludeDispenseErrors', 'true');
            }
            if (filters?.includeOnHold) {
                params.append('includeOnHold', 'true');
            }
            if (filters?.onHoldSubStatusId != null) {
                params.append('onHoldSubStatusId', String(filters.onHoldSubStatusId));
            }
            if (filters?.showFutureDated) {
                params.append('showFutureDated', 'true');
            }

            const url = params.toString() ? `/api/AutoMode/next?${params.toString()}` : '/api/AutoMode/next';
            const response = await axios.get<AutoModeNextItemResponse>(url);

            Vue.prototype.$store.lastAutoModeResponse = response.data;

            return response.data;
        } catch (error) {
            console.error('Error getting next auto mode item:', error);
            throw error;
        }
    }

    // --- Queue filters ---

    static getSavedSpecificQueues(): string[] {
        const raw = getUserSetting(AUTO_MODE_SPECIFIC_QUEUES_KEY);
        if (!raw) return [];
        try {
            return JSON.parse(raw) as string[];
        } catch {
            return [];
        }
    }

    static saveSpecificQueues(queues: string[]): void {
        if (!queues.length) {
            deleteUserSetting(AUTO_MODE_SPECIFIC_QUEUES_KEY);
        } else {
            saveUserSetting(AUTO_MODE_SPECIFIC_QUEUES_KEY, JSON.stringify(queues));
        }
    }

    // --- Store filters ---

    static getSavedStoreFilters(): AutoModeFilterOption[] {
        const raw = getUserSetting(AUTO_MODE_STORE_FILTERS_KEY);
        if (!raw) return [];
        try {
            return JSON.parse(raw) as AutoModeFilterOption[];
        } catch {
            return [];
        }
    }

    static saveStoreFilters(stores: AutoModeFilterOption[]): void {
        if (!stores.length) {
            deleteUserSetting(AUTO_MODE_STORE_FILTERS_KEY);
        } else {
            saveUserSetting(AUTO_MODE_STORE_FILTERS_KEY, JSON.stringify(stores));
        }
    }

    // --- Program filters ---

    static getSavedProgramFilters(): AutoModeFilterOption[] {
        const raw = getUserSetting(AUTO_MODE_PROGRAM_FILTERS_KEY);
        if (!raw) return [];
        try {
            return JSON.parse(raw) as AutoModeFilterOption[];
        } catch {
            return [];
        }
    }

    static saveProgramFilters(programs: AutoModeFilterOption[]): void {
        if (!programs.length) {
            deleteUserSetting(AUTO_MODE_PROGRAM_FILTERS_KEY);
        } else {
            saveUserSetting(AUTO_MODE_PROGRAM_FILTERS_KEY, JSON.stringify(programs));
        }
    }

    // --- Exclude dispense errors (PV1) ---

    static getSavedExcludeDispenseErrors(): boolean {
        return getUserSetting(AUTO_MODE_EXCLUDE_DISPENSE_ERRORS_KEY) === 'true';
    }

    static saveExcludeDispenseErrors(exclude: boolean): void {
        if (exclude) {
            saveUserSetting(AUTO_MODE_EXCLUDE_DISPENSE_ERRORS_KEY, 'true');
        } else {
            deleteUserSetting(AUTO_MODE_EXCLUDE_DISPENSE_ERRORS_KEY);
        }
    }

    /**
     * Get the complete saved filters
     */
    static getSavedFilters(): AutoModeFilters {
        const queues = AutoModeService.getSavedSpecificQueues();
        const stores = AutoModeService.getSavedStoreFilters();
        const programs = AutoModeService.getSavedProgramFilters();
        const excludeDispenseErrors = AutoModeService.getSavedExcludeDispenseErrors();
        return {
            queues: queues.length ? queues : undefined,
            storeIds: stores.length ? stores.map(s => s.id) : undefined,
            programIds: programs.length ? programs.map(p => p.id) : undefined,
            excludeDispenseErrors: excludeDispenseErrors || undefined,
        };
    }

    /**
     * Clear all saved filter preferences
     */
    static clearFilters(): void {
        deleteUserSetting(AUTO_MODE_SPECIFIC_QUEUES_KEY);
        deleteUserSetting(AUTO_MODE_STORE_FILTERS_KEY);
        deleteUserSetting(AUTO_MODE_PROGRAM_FILTERS_KEY);
        deleteUserSetting(AUTO_MODE_EXCLUDE_DISPENSE_ERRORS_KEY);
    }
}

export default AutoModeService;
